import { useMemo, useState } from "react";

import {
  CloseIcon,
  DownloadIcon,
  FileUploadFillIcon,
  Icon,
} from "@vericus/cadmus-icons";
import {
  Button,
  Checkbox,
  ControlButton,
  fileTypeToIconMap,
  getFileType,
  LinkButtonV2,
  Modal,
  Pill,
  Text,
  Tooltip,
  TooltipProvider,
  vars,
} from "@vericus/cadmus-ui";

import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  type RowSelectionState,
  useReactTable,
} from "@tanstack/react-table";

import { useDownloadWorkAttachments } from "@/features/feedback/hooks/use-download-work-attachments";
import { BlockAttachmentFragment } from "@/generated/graphql";
import { formatDate } from "@/utils/datetime";
import { bulkDownloadWarning, formatBytes } from "@/utils/file-utils";

import * as styles from "./work-attachments-modal.css";

const COMPACT_DATE_FORMAT = "d MMM, h:mm aaa";

const columnHelper = createColumnHelper<BlockAttachmentFragment>();

const createColumns = (
  onDownload: (attachment: BlockAttachmentFragment) => void
) => [
  columnHelper.display({
    id: "select",
    size: 48,
    header: ({ table }) => {
      const allSelected = table.getIsAllRowsSelected();
      const someSelected = table.getIsSomeRowsSelected();
      return (
        <Checkbox
          checked={someSelected || allSelected}
          mixed={someSelected}
          onChange={table.getToggleAllRowsSelectedHandler()}
          aria-label="Select all"
        />
      );
    },
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onChange={row.getToggleSelectedHandler()}
        aria-label={`Select ${row.original.name}`}
      />
    ),
  }),
  columnHelper.accessor((row) => row, {
    id: "file",
    header: () => (
      <Text kind="label" style={{ marginLeft: 8 }}>
        FILE NAME
      </Text>
    ),
    cell: ({ row }) => (
      <div className={styles.fileNameCell}>
        <Icon
          iconName={fileTypeToIconMap[getFileType(row.original.name)]}
          style={{ width: 24, height: 24 }}
        />
        <LinkButtonV2
          className={styles.fileNameLink}
          style={{ color: vars.colors.lilac500 }}
          onClick={() =>
            row.original.mediaUrl &&
            window.open(row.original.mediaUrl, "_blank", "noopener,noreferrer")
          }
        >
          {row.original.name}
        </LinkButtonV2>
        {row.original.fileSize && (
          <Text kind="systemSm" style={{ flexShrink: 0 }}>
            {formatBytes(row.original.fileSize)}
          </Text>
        )}
      </div>
    ),
  }),
  columnHelper.display({
    id: "uploadDate",
    size: 160,
    header: () => (
      <Text kind="label" style={{ marginLeft: 8 }}>
        UPLOAD DATE
      </Text>
    ),
    cell: ({ row }) => (
      <Text kind="bodyMd" className={styles.dateCell}>
        {row.original.insertedAt
          ? formatDate(row.original.insertedAt, COMPACT_DATE_FORMAT)
          : "—"}
      </Text>
    ),
  }),
  columnHelper.display({
    id: "uploadedBy",
    size: 140,
    header: () => (
      <Text kind="label" style={{ marginLeft: 8 }}>
        UPLOAD BY
      </Text>
    ),
    cell: ({ row }) => (
      <Pill style={{ marginLeft: 8 }}>
        {row.original.uploadedBy?.name ?? "—"}
      </Pill>
    ),
  }),
  columnHelper.display({
    id: "actions",
    size: 48,
    header: () => null,
    cell: ({ row }) => (
      <ControlButton
        icon={<DownloadIcon size={24} label="Download" />}
        onClick={() => onDownload(row.original)}
        aria-label={`Download ${row.original.name}`}
      />
    ),
  }),
];

interface Props {
  isOpen: boolean;
  onClose: () => void;
  answerBlockId: string;
  attachments: BlockAttachmentFragment[];
}

/**
 * Modal to display all uploaded file attachments in a table layout.
 *
 * For Aphrodite (student view):
 * - Checkbox selection, FILE NAME, UPLOAD DATE, UPLOAD BY columns
 * - Download action per row
 * - Sorted by latest upload on top
 */
export function WorkAttachmentsModal({
  isOpen,
  onClose,
  answerBlockId,
  attachments,
}: Props) {
  const [rowSelection, setRowSelection] = useState<RowSelectionState>({});
  const downloadWorkAttachments = useDownloadWorkAttachments();

  const handleDownload = (attachment: BlockAttachmentFragment) => {
    downloadWorkAttachments({
      answerBlockId,
      blockAttachmentIds: [attachment.id],
    });
  };

  const handleDownloadAll = () => {
    downloadWorkAttachments({
      answerBlockId,
      blockAttachmentIds: attachments.map((a) => a.id),
    });
  };

  const handleDownloadSelected = () => {
    const selectedIds = table
      .getSelectedRowModel()
      .rows.map((row) => row.original.id);
    downloadWorkAttachments({
      answerBlockId,
      blockAttachmentIds: selectedIds,
    });
  };

  // Sort attachments by upload date, latest on top
  const sortedAttachments = useMemo(() => {
    return [...attachments].sort((a, b) => {
      if (!a.insertedAt && !b.insertedAt) return 0;
      if (!a.insertedAt) return 1;
      if (!b.insertedAt) return -1;
      return (
        new Date(b.insertedAt).getTime() - new Date(a.insertedAt).getTime()
      );
    });
  }, [attachments]);

  const columns = createColumns(handleDownload);

  const table = useReactTable({
    data: sortedAttachments,
    columns,
    getRowId: (row) => row.id,
    state: { rowSelection },
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    defaultColumn: { size: Number.MAX_SAFE_INTEGER },
  });

  return (
    <TooltipProvider>
      <Modal.Root open={isOpen} onOpenChange={onClose}>
        <Modal.Content
          width={900}
          withCloseButton={false}
          className={styles.modalWrapper}
        >
          <div className={styles.modalContent}>
            {/* Header */}
            <div className={styles.header}>
              <div className={styles.headerLeft}>
                <div className={styles.iconContainer}>
                  <FileUploadFillIcon
                    size={24}
                    label="Upload"
                    className={styles.headerIcon}
                  />
                </div>
                <Modal.Title asChild>
                  <Text kind="headingFive">Files submitted</Text>
                </Modal.Title>
              </div>
              <Modal.Close asChild>
                <ControlButton
                  aria-label="Close modal"
                  icon={<CloseIcon size={24} label="Close" />}
                />
              </Modal.Close>
            </div>

            {/* Subheader */}
            <div className={styles.subheader}>
              <Text kind="headingSix">{attachments.length} files</Text>
              {attachments.length > 0 &&
                (() => {
                  const selectedCount = Object.keys(rowSelection).length;
                  const hasSelection =
                    selectedCount > 0 && selectedCount < attachments.length;
                  const downloadSet = hasSelection
                    ? table
                        .getSelectedRowModel()
                        .rows.map((row) => row.original)
                    : attachments;
                  const warning = bulkDownloadWarning(downloadSet);
                  const button = (
                    <Button
                      kind={warning != null ? "error" : "outline"}
                      size="md"
                      icon={<DownloadIcon label="download" size={24} />}
                      onClick={
                        hasSelection
                          ? handleDownloadSelected
                          : handleDownloadAll
                      }
                    >
                      {hasSelection
                        ? `Download ${selectedCount} ${selectedCount === 1 ? "file" : "files"}`
                        : "Download All"}
                    </Button>
                  );
                  return warning != null ? (
                    <Tooltip content={warning}>{button}</Tooltip>
                  ) : (
                    button
                  );
                })()}
            </div>

            {/* Table */}
            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  {table.getHeaderGroups().map((hg) => (
                    <tr key={hg.id}>
                      {hg.headers.map((header) => (
                        <th
                          key={header.id}
                          className={styles.tableHeader}
                          colSpan={header.colSpan}
                        >
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                                header.column.columnDef.header,
                                header.getContext()
                              )}
                        </th>
                      ))}
                    </tr>
                  ))}
                </thead>
                <tbody>
                  {table.getRowModel().rows.map((row) => (
                    <tr key={row.id} className={styles.tableRow}>
                      {row.getVisibleCells().map((cell) => (
                        <td key={cell.id} className={styles.tableCell}>
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext()
                          )}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </Modal.Content>
      </Modal.Root>
    </TooltipProvider>
  );
}
